/**
 * Admin Stats Page
 * Dashboard with system statistics and metrics
 */
import React, { useState, useEffect } from 'react';
import { Box, Flex, Heading, Button, Grid, GridItem, Text, useToast, Spinner, VStack, HStack, Stat, StatLabel, StatNumber, StatHelpText, Divider } from '@chakra-ui/react';
import { RefreshCw, Users, UserCheck, UserX, Shield, Mail, TrendingUp } from 'lucide-react';
import { adminService, type AdminStatsResponse } from '../../services/AdminService';

export const StatsPage: React.FC = () => {
  const toast = useToast();

  // State
  const [stats, setStats] = useState<AdminStatsResponse | null>(null);
  const [loading, setLoading] = useState(true);

  // Load stats
  const loadStats = async () => {
    try {
      setLoading(true);
      const response = await adminService.getStats();
      setStats(response);
    } catch (error: any) {
      toast({
        title: 'Error loading statistics',
        description: error.response?.data?.detail || 'Failed to load stats',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    loadStats();
  }, []);

  // Handle refresh
  const handleRefresh = () => {
    loadStats();
  };

  if (loading) {
    return (
      <Flex justify="center" align="center" h="400px">
        <Spinner size="xl" color="blue.500" />
      </Flex>
    );
  }

  if (!stats) {
    return (
      <Flex justify="center" align="center" h="400px">
        <Text fontSize="lg" color="gray.500">No statistics available</Text>
      </Flex>
    );
  }

  return (
    <Box p={6}>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Flex justify="flex-end" align="center">
          <Button
            leftIcon={<RefreshCw size={16} />}
            onClick={handleRefresh}
            size="sm"
            variant="outline"
          >
            Refresh
          </Button>
        </Flex>

        {/* User Statistics */}
        <Box>
          <Heading size="md" mb={4}>
            User Statistics
          </Heading>
          <Grid templateColumns="repeat(auto-fit, minmax(250px, 1fr))" gap={4}>
            {/* Total Users */}
            <GridItem>
              <Box bg="white" p={6} borderRadius="md" shadow="sm">
                <HStack spacing={3} mb={3}>
                  <Box p={2} bg="blue.100" borderRadius="md">
                    <Users size={20} color="#3182CE" />
                  </Box>
                  <Text fontSize="sm" fontWeight="medium" color="gray.600">
                    Total Users
                  </Text>
                </HStack>
                <Text fontSize="3xl" fontWeight="bold">
                  {stats.users.total}
                </Text>
              </Box>
            </GridItem>

            {/* Active Users */}
            <GridItem>
              <Box bg="white" p={6} borderRadius="md" shadow="sm">
                <HStack spacing={3} mb={3}>
                  <Box p={2} bg="green.100" borderRadius="md">
                    <UserCheck size={20} color="#38A169" />
                  </Box>
                  <Text fontSize="sm" fontWeight="medium" color="gray.600">
                    Active Users
                  </Text>
                </HStack>
                <Text fontSize="3xl" fontWeight="bold">
                  {stats.users.active}
                </Text>
                <Text fontSize="sm" color="gray.500">
                  {((stats.users.active / stats.users.total) * 100).toFixed(1)}% of total
                </Text>
              </Box>
            </GridItem>

            {/* Blocked Users */}
            <GridItem>
              <Box bg="white" p={6} borderRadius="md" shadow="sm">
                <HStack spacing={3} mb={3}>
                  <Box p={2} bg="red.100" borderRadius="md">
                    <UserX size={20} color="#E53E3E" />
                  </Box>
                  <Text fontSize="sm" fontWeight="medium" color="gray.600">
                    Blocked Users
                  </Text>
                </HStack>
                <Text fontSize="3xl" fontWeight="bold">
                  {stats.users.blocked}
                </Text>
                <Text fontSize="sm" color="gray.500">
                  {((stats.users.blocked / stats.users.total) * 100).toFixed(1)}% of total
                </Text>
              </Box>
            </GridItem>

            {/* Admins */}
            <GridItem>
              <Box bg="white" p={6} borderRadius="md" shadow="sm">
                <HStack spacing={3} mb={3}>
                  <Box p={2} bg="purple.100" borderRadius="md">
                    <Shield size={20} color="#805AD5" />
                  </Box>
                  <Text fontSize="sm" fontWeight="medium" color="gray.600">
                    Administrators
                  </Text>
                </HStack>
                <Text fontSize="3xl" fontWeight="bold">
                  {stats.users.admins}
                </Text>
                <Text fontSize="sm" color="gray.500">
                  {((stats.users.admins / stats.users.total) * 100).toFixed(1)}% of total
                </Text>
              </Box>
            </GridItem>
          </Grid>
        </Box>

        {/* Registration Statistics */}
        <Box>
          <Heading size="md" mb={4}>
            Registration Activity
          </Heading>
          <Grid templateColumns="repeat(auto-fit, minmax(250px, 1fr))" gap={4}>
            {/* Today */}
            <GridItem>
              <Box bg="white" p={6} borderRadius="md" shadow="sm">
                <Stat>
                  <StatLabel>Registered Today</StatLabel>
                  <StatNumber>{stats.users.registered_today}</StatNumber>
                  <StatHelpText>
                    <TrendingUp size={14} style={{ display: 'inline', marginRight: '4px' }} />
                    New users today
                  </StatHelpText>
                </Stat>
              </Box>
            </GridItem>

            {/* This Week */}
            <GridItem>
              <Box bg="white" p={6} borderRadius="md" shadow="sm">
                <Stat>
                  <StatLabel>Registered This Week</StatLabel>
                  <StatNumber>{stats.users.registered_this_week}</StatNumber>
                  <StatHelpText>Last 7 days</StatHelpText>
                </Stat>
              </Box>
            </GridItem>

            {/* This Month */}
            <GridItem>
              <Box bg="white" p={6} borderRadius="md" shadow="sm">
                <Stat>
                  <StatLabel>Registered This Month</StatLabel>
                  <StatNumber>{stats.users.registered_this_month}</StatNumber>
                  <StatHelpText>Last 30 days</StatHelpText>
                </Stat>
              </Box>
            </GridItem>
          </Grid>
        </Box>

        {/* Invite Statistics */}
        <Box>
          <Heading size="md" mb={4}>
            Invite Statistics
          </Heading>
          <Grid templateColumns="repeat(auto-fit, minmax(250px, 1fr))" gap={4}>
            {/* Total Invites */}
            <GridItem>
              <Box bg="white" p={6} borderRadius="md" shadow="sm">
                <HStack spacing={3} mb={3}>
                  <Box p={2} bg="blue.100" borderRadius="md">
                    <Mail size={20} color="#3182CE" />
                  </Box>
                  <Text fontSize="sm" fontWeight="medium" color="gray.600">
                    Total Invites
                  </Text>
                </HStack>
                <Text fontSize="3xl" fontWeight="bold">
                  {stats.invites.total}
                </Text>
              </Box>
            </GridItem>

            {/* Available Invites */}
            <GridItem>
              <Box bg="white" p={6} borderRadius="md" shadow="sm">
                <HStack spacing={3} mb={3}>
                  <Box p={2} bg="green.100" borderRadius="md">
                    <Mail size={20} color="#38A169" />
                  </Box>
                  <Text fontSize="sm" fontWeight="medium" color="gray.600">
                    Available Invites
                  </Text>
                </HStack>
                <Text fontSize="3xl" fontWeight="bold">
                  {stats.invites.available}
                </Text>
                <Text fontSize="sm" color="gray.500">
                  {((stats.invites.available / stats.invites.total) * 100).toFixed(1)}% unused
                </Text>
              </Box>
            </GridItem>

            {/* Used Invites */}
            <GridItem>
              <Box bg="white" p={6} borderRadius="md" shadow="sm">
                <HStack spacing={3} mb={3}>
                  <Box p={2} bg="purple.100" borderRadius="md">
                    <Mail size={20} color="#805AD5" />
                  </Box>
                  <Text fontSize="sm" fontWeight="medium" color="gray.600">
                    Used Invites
                  </Text>
                </HStack>
                <Text fontSize="3xl" fontWeight="bold">
                  {stats.invites.used}
                </Text>
                <Text fontSize="sm" color="gray.500">
                  {stats.invites.usage_rate.toFixed(1)}% usage rate
                </Text>
              </Box>
            </GridItem>
          </Grid>
        </Box>

        {/* Registration Chart (if available) */}
        {stats.registrations_chart && stats.registrations_chart.length > 0 && (
          <Box bg="white" p={6} borderRadius="md" shadow="sm">
            <Heading size="md" mb={4}>
              Registration Trend
            </Heading>
            <Text fontSize="sm" color="gray.500">
              Chart visualization coming soon...
            </Text>
            <Divider my={4} />
            <VStack align="stretch" spacing={2}>
              {stats.registrations_chart.slice(0, 7).map((item) => (
                <HStack key={item.date} justify="space-between">
                  <Text fontSize="sm" color="gray.600">
                    {item.date}
                  </Text>
                  <Text fontSize="sm" fontWeight="medium">
                    {item.count} registrations
                  </Text>
                </HStack>
              ))}
            </VStack>
          </Box>
        )}
      </VStack>
    </Box>
  );
};

export default StatsPage;
